/**
 * One place that turns site-api's verification-code errors into a stable reason (for Heap) and
 * into the copy users see, so every phone screen says the same thing for the same failure.
 */

const ERROR_REASONS: [RegExp, string][] = [
  [/Did not find matching verification code/i, 'InvalidCode'],
  [/Too many failed attempts/i, 'TooManyAttempts'],
  [/Too many requests|rate limit exceeded/i, 'RateLimited'],
  [/No verification code found/i, 'CodeNotFound'],
  [/already associated with another user/i, 'PhoneInUse'],
  [/Cannot initiate sign-up for registered user/i, 'AlreadyRegistered'],
  [/Verification (check|request) failed|missing verification ID/i, 'ProviderError'],
];

/** A stable reason for a failed send or check: the `HCAPTCHA_*` code, a known case, or `Other`. */
export const getVerificationErrorReason = (error?: string) => {
  const hcaptchaCode = error?.match(/HCAPTCHA_[A-Z_]+/)?.[0];
  if (hcaptchaCode) return hcaptchaCode;
  return ERROR_REASONS.find(([pattern]) => error && pattern.test(error))?.[1] ?? 'Other';
};

const VERIFICATION_UNAVAILABLE =
  'Verification is temporarily unavailable. Please try again shortly.';
const PHONE_IN_USE = 'User already exists with this phone number.';

const SEND_ERROR_MESSAGES: Record<string, string> = {
  AlreadyRegistered: PHONE_IN_USE,
  HCAPTCHA_MISCONFIGURED: VERIFICATION_UNAVAILABLE,
  HCAPTCHA_REQUIRED: 'Please complete the human verification to continue.',
  HCAPTCHA_SERVICE_UNAVAILABLE: VERIFICATION_UNAVAILABLE,
  HCAPTCHA_VERIFICATION_FAILED: 'Human verification failed. Please check the box again.',
  PhoneInUse: PHONE_IN_USE,
  RateLimited: 'Too many attempts. Please wait a moment and try again.',
};

/** What to tell the user when sending (or resending) a verification code fails. */
export const getPhoneSendErrorMessage = (error?: string) =>
  SEND_ERROR_MESSAGES[getVerificationErrorReason(error)] ??
  'Phone number verification failed. Please try again.';
